import { useEffect, useRef, useState } from "react";
import { getBlobImageSrc } from "../../utils/common";

/**
 * Resolves a pre-signed URL to a blob URL and cleans up object URLs on changes.
 *
 * Args:
 *   url: Optional pre-signed URL to resolve.
 *
 * Returns:
 *   string | undefined: Blob URL for rendering the asset.
 *
 * Notes:
 *   Revokes previously created object URLs to prevent memory leaks.
 */
export const useResolvedBlobUrl = (url?: string) => {
  const [src, setSrc] = useState<string>();
  const lastObjUrl = useRef<string>();

  useEffect(() => {
    let killed = false;

    (async () => {
      if (!url) {
        setSrc(undefined);
        return;
      }
      const resolved = await getBlobImageSrc(url);
      if (killed) return;

      if (lastObjUrl.current && lastObjUrl.current !== resolved) {
        URL.revokeObjectURL(lastObjUrl.current);
      }
      lastObjUrl.current = resolved;
      setSrc(resolved);
    })();

    return () => {
      killed = true;
      if (lastObjUrl.current) {
        URL.revokeObjectURL(lastObjUrl.current);
        lastObjUrl.current = undefined;
      }
    };
  }, [url]);

  return src;
};
